import { memo } from 'react';

import { ApolloProvider } from '@apollo/client';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { LoginCallback, SecureRoute, Security } from '@okta/okta-react';
import './root.scss';

import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { LOGIN_SCREEN_ROUTE, Routes } from './config';
import { DependencyContainer } from './DependencyContainer';
import Configuration from './features/Configuration/containers/Configuration';
import HealthSummary from './features/HealthSummary/containers/HealthSummary';
import LoginCallbackError from './features/login/components/LoginCallbackError';
import LoginContainer from './features/login/containers/LoginContainer';
import MonitorAdd from './features/Monitor/containers/MonitorAdd';
import MonitorInfo from './features/Monitor/containers/MonitorInfo';
import Monitors from './features/Monitor/containers/Monitors';
import OutreachContainer from './features/Monitor/containers/OutreachContainer';
import ForAuthenticated from './ForAuthenticated';
import AppLayout from './layouts/AppLayout';
import getOktaProvider from './OktaProvider';
import { getGraphqlClient } from './utils/graphqlConfig';
import { useRedirectToRoute } from './utils/hooks/useRedirectToRoute';
import AutonomousMonitorDetails from './features/Monitor/containers/AutonomousMonitorDetails';
import GlobalDemoProvider from './components/GlobalDemo/GlobalDemo';
import AccessNetworkMonitorDetails from './features/Monitor/DEMO_AccessNetworkMonitors/AccessNetworkMonitorDetails';

const oktaAuth = getOktaProvider();

const { loginService } = new DependencyContainer();
const restoreOriginalUri = async (oktaAuth: OktaAuth, originalUri: string) => {
  const targetRoute = loginService.getTargetRoute();
  if (targetRoute) {
    window.location.href = targetRoute;
    loginService.removeTargetRoute();
    return;
  }
  const url = originalUri
    ? toRelativeUrl(originalUri, window.location.origin)
    : Routes.Index;
  window.location.href = url;
};

const AppContainer = () => {
  const { pathname } = useLocation();
  const redirectToRoute = useRedirectToRoute();

  const onAuthRequired = () => {
    if (pathname === LOGIN_SCREEN_ROUTE) {
      return;
    }
    loginService.setTargetRoute(pathname);
    redirectToRoute(LOGIN_SCREEN_ROUTE);
  };

  return (
    <Security
      oktaAuth={oktaAuth}
      onAuthRequired={onAuthRequired}
      restoreOriginalUri={restoreOriginalUri}
    >
      <ApolloProvider client={getGraphqlClient()}>
        <GlobalDemoProvider>
          <div className="AppContainer">
            <Switch>
              <SecureRoute
                exact
                path={Routes.HealthSummary}
                component={HealthSummary}
              >
                <ForAuthenticated>
                  <AppLayout>
                    <HealthSummary />
                  </AppLayout>
                </ForAuthenticated>
              </SecureRoute>

              <SecureRoute
                exact
                path={Routes.MonitorEdit}
                component={MonitorInfo}
              >
                <ForAuthenticated>
                  <AppLayout>
                    <MonitorAdd />
                  </AppLayout>
                </ForAuthenticated>
              </SecureRoute>

              <SecureRoute
                exact
                path={Routes.MonitorAdd}
                component={MonitorAdd}
              >
                <ForAuthenticated>
                  <AppLayout>
                    <MonitorAdd />
                  </AppLayout>
                </ForAuthenticated>
              </SecureRoute>

              <SecureRoute
                path={Routes.AutonomousMonitorDetails}
                component={AutonomousMonitorDetails}
              >
                <ForAuthenticated>
                  <AppLayout>
                    <AutonomousMonitorDetails />
                  </AppLayout>
                </ForAuthenticated>
              </SecureRoute>

              <SecureRoute
                path={Routes.AccessNetworkMonitorDetails}
                component={AccessNetworkMonitorDetails}
              >
                <ForAuthenticated>
                  <AppLayout>
                    <AccessNetworkMonitorDetails />
                  </AppLayout>
                </ForAuthenticated>
              </SecureRoute>

              <SecureRoute path={Routes.MonitorDetails} component={MonitorInfo}>
                <ForAuthenticated>
                  <AppLayout>
                    <MonitorInfo />
                  </AppLayout>
                </ForAuthenticated>
              </SecureRoute>

              <SecureRoute exact path={Routes.MonitorsTab} component={Monitors}>
                <ForAuthenticated>
                  <AppLayout>
                    <Monitors />
                  </AppLayout>
                </ForAuthenticated>
              </SecureRoute>

              <SecureRoute exact path={Routes.Monitors} component={Monitors}>
                <ForAuthenticated>
                  <AppLayout>
                    <Monitors />
                  </AppLayout>
                </ForAuthenticated>
              </SecureRoute>

              <SecureRoute exact path={Routes.OutreachEdit}>
                <ForAuthenticated>
                  <AppLayout>
                    <OutreachContainer />
                  </AppLayout>
                </ForAuthenticated>
              </SecureRoute>

              <SecureRoute
                path={Routes.Configuration}
                component={Configuration}
              >
                <ForAuthenticated>
                  <AppLayout>
                    <Configuration />
                  </AppLayout>
                </ForAuthenticated>
              </SecureRoute>

              <Route path={Routes.Login} component={LoginContainer} />
              <Route path={Routes.LoginCallback}>
                <LoginCallback errorComponent={LoginCallbackError} />
              </Route>
              <SecureRoute exact path={Routes.Index}>
                <AppLayout>
                  <HealthSummary />
                </AppLayout>
              </SecureRoute>
              <Route>
                <Redirect to={Routes.HealthSummary} />
              </Route>
            </Switch>
          </div>
        </GlobalDemoProvider>
      </ApolloProvider>
    </Security>
  );
};

export default memo(AppContainer);
